/*import React, { useState } from 'react';
import { Card, Button, Form, InputGroup } from 'react-bootstrap';

const Chatbot = ({ packageAnnouncement, onPackageAnnounced }) => {
  const [messages, setMessages] = useState([
    { sender: 'bot', text: 'Hi! How can I help you with your wedding plans?' }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setMessages(msgs => [...msgs, { sender: 'user', text: input }]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch('/api/chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: input })
      });
      const data = await res.json();
      setMessages(msgs => [...msgs, { sender: 'bot', text: data.reply }]);
    } catch {
      setMessages(msgs => [...msgs, { sender: 'bot', text: 'Sorry, there was an error.' }]);
    }
    setLoading(false);
  };

  return (
    <Card className="mb-4">
      <Card.Header>Ask BALIV-AI-P</Card.Header>
      <Card.Body style={{ maxHeight: 300, overflowY: 'auto' }}>
        {messages.map((msg, idx) => (
          <div key={idx} className={msg.sender === 'user' ? 'text-end' : 'text-start'}>
            <span className={msg.sender === 'user' ? 'bg-primary text-white p-2 rounded' : 'bg-light p-2 rounded'}>
              {msg.text}
            </span>
          </div>
        ))}
        {loading && <div className="text-muted">Thinking...</div>}
      </Card.Body>
      <Card.Footer>
        <Form onSubmit={sendMessage}>
          <InputGroup>
            <Form.Control
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder="Type your question..."
              disabled={loading}
            />
            <Button type="submit" disabled={loading}>Send</Button>
          </InputGroup>
        </Form>
      </Card.Footer>
    </Card>
  );
};

export default Chatbot;*/

import React, { useState, useRef, useEffect } from 'react';
import { Card, Form, Button, Spinner } from 'react-bootstrap';

const Chatbot = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! How can I help you with your wedding plans?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // When a package is selected elsewhere, show a typewriter bot message listing vendors
  useEffect(() => {
    if (!packageAnnouncement || !packageAnnouncement.vendors) return;

    const vendors = packageAnnouncement.vendors;
    const pkgName = packageAnnouncement.name || 'Selected Package';
    const fullText = `You've selected ${pkgName}. Suggested vendors: ${vendors.join(', ')}.`;

    const id = Date.now();
    const botMessage = {
      id,
      text: '',
      sender: 'bot',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, botMessage]);

    let i = 0;
    const speed = 12; // ms per character (typewriter)
    const interval = setInterval(() => {
      i += 1;
      setMessages(prev => prev.map(m => m.id === id ? { ...m, text: fullText.slice(0, i) } : m));
      if (i >= fullText.length) {
        clearInterval(interval);
        // notify parent that announcement finished (optional)
        if (typeof onPackageAnnounced === 'function') onPackageAnnounced();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [packageAnnouncement]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await fetch('/api/chatbot', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: inputValue }),
      });

      const data = await response.json();
      
      const botMessage = {
        id: Date.now() + 1,
        text: data.reply,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: "Sorry, I'm having trouble connecting. Please try again.",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div style={{ height: '600px', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div className="bg-primary text-white p-3 text-center">
        <h5 className="mb-1">🤖 Ask BALIV-AI-P</h5>
        <small>Your Bali Wedding Planning Assistant</small>
      </div>

      {/* Messages Area */}
      <div 
        className="flex-grow-1 p-3 overflow-auto"
        style={{ 
          backgroundColor: '#f8f9fa',
          maxHeight: '400px'
        }}
      >
        {messages.map((message) => (
          <div key={message.id} className={`mb-3 d-flex ${message.sender === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
            <div 
              className={`p-3 rounded-3 shadow-sm ${
                message.sender === 'user' 
                  ? 'bg-primary text-white' 
                  : 'bg-white border'
              }`}
              style={{ maxWidth: '75%' }}
            >
              <p className="mb-1">{message.text}</p>
              <small className={`${message.sender === 'user' ? 'text-white-50' : 'text-muted'}`}>
                {message.timestamp.toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </small>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="mb-3 d-flex justify-content-start">
            <div className="bg-white border p-3 rounded-3 shadow-sm">
              <Spinner animation="grow" size="sm" className="me-2" />
              <small className="text-muted">BALIV-AI-P is thinking...</small>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="p-3 bg-white border-top">
        <div className="d-flex gap-2">
          <Form.Control
            as="textarea"
            rows={1}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your question..."
            disabled={isLoading}
            className="resize-none"
          />
          <Button 
            variant="primary"
            onClick={sendMessage} 
            disabled={!inputValue.trim() || isLoading}
            style={{ minWidth: '80px' }}
          >
            {isLoading ? <Spinner animation="border" size="sm" /> : 'Send'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;